/**
 * Custom neutral dark theme overrides for Eva Design System.
 *
 * The default Eva dark theme uses blue-tinted grays (#222B45, #1A2138, etc.)
 * which look cold and blueish. This override replaces the entire color-basic
 * scale with neutral grays inspired by iOS / Material dark mode.
 *
 * Usage:
 *   import * as eva from '@eva-design/eva'
 *   import { customDarkTheme } from '../theme/customDarkTheme'
 *   <ApplicationProvider {...eva} theme={{ ...eva.dark, ...customDarkTheme }}>
 *
 * Because Eva resolves semantic tokens ($color-basic-800, etc.) from the
 * color-basic scale, overriding these base values cascades through all
 * background-*, border-*, and text-* tokens automatically.
 */

export const customDarkTheme = {
  // ── Neutral color-basic scale ──────────────────────────────────────
  'color-basic-100': '#FFFFFF',   // text / controls on dark surfaces
  'color-basic-200': '#F2F2F7',   // lightest alternative surface
  'color-basic-300': '#E5E5EA',   // light borders / alt backgrounds
  'color-basic-400': '#D1D1D6',   // medium-light
  'color-basic-500': '#AEAEB2',   // medium (disabled borders, etc.)
  'color-basic-600': '#8E8E93',   // hint text, subtle icons
  'color-basic-700': '#48484A',   // elevated surface / dark dividers
  'color-basic-800': '#2C2C2E',   // primary surface (cards, modals)
  'color-basic-900': '#1C1C1E',   // secondary / grouped background
  'color-basic-1000': '#141414',  // deeper background
  'color-basic-1100': '#0A0A0A',  // deepest background / root

  // ── Transparent variants (neutral base instead of blue-tinted) ─────
  'color-basic-transparent-100': 'rgba(142, 142, 147, 0.08)',
  'color-basic-transparent-200': 'rgba(142, 142, 147, 0.16)',
  'color-basic-transparent-300': 'rgba(142, 142, 147, 0.24)',
  'color-basic-transparent-400': 'rgba(142, 142, 147, 0.32)',
  'color-basic-transparent-500': 'rgba(142, 142, 147, 0.40)',
  'color-basic-transparent-600': 'rgba(142, 142, 147, 0.48)',

  // ── Muted primary scale (desaturated steel blue for dark mode) ──────
  'color-primary-100': '#ECEEF5',
  'color-primary-200': '#D2D8EA',
  'color-primary-300': '#B0BBDA',
  'color-primary-400': '#95A7D0',
  'color-primary-500': '#7B93C8',   // muted steel blue (was #3366FF)
  'color-primary-600': '#6479AB',
  'color-primary-700': '#4E608E',
  'color-primary-800': '#3A4972',
  'color-primary-900': '#283356',
  'color-primary-transparent-100': 'rgba(123, 147, 200, 0.08)',
  'color-primary-transparent-200': 'rgba(123, 147, 200, 0.16)',
  'color-primary-transparent-300': 'rgba(123, 147, 200, 0.24)',
  'color-primary-transparent-400': 'rgba(123, 147, 200, 0.32)',
  'color-primary-transparent-500': 'rgba(123, 147, 200, 0.40)',
  'color-primary-transparent-600': 'rgba(123, 147, 200, 0.48)',
};
